import type { SourceAuth, SourceId } from '../types.ts';
import type { VeneraPluginStorage } from './types.ts';

const PLUGIN_STORAGE_KEY = 'veneraPlugin';
const PLUGIN_STORAGE_SOURCE_KEY = 'veneraPluginSource';

function cloneStore(store: Record<string, unknown>): Record<string, unknown> {
  return { ...store };
}

function readSource(auth: SourceAuth | undefined): string | undefined {
  const value = auth?.extra?.[PLUGIN_STORAGE_SOURCE_KEY];
  return typeof value === 'string' ? value : undefined;
}

function readStore(auth: SourceAuth | undefined): Record<string, unknown> {
  const value = auth?.extra?.[PLUGIN_STORAGE_KEY];
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return { ...(value as Record<string, unknown>) };
}

function assertSourceMatches(sourceId: SourceId, auth: SourceAuth | undefined): void {
  const storedSource = readSource(auth);
  if (storedSource === undefined && auth?.extra?.[PLUGIN_STORAGE_KEY] === undefined) return;
  if (storedSource !== sourceId) {
    throw new Error(`Storage source mismatch: expected ${sourceId}, got ${storedSource}`);
  }
}

function withStore(
  sourceId: SourceId,
  auth: SourceAuth | undefined,
  store: Record<string, unknown>,
): SourceAuth {
  const extra = { ...(auth?.extra ?? {}) };
  extra[PLUGIN_STORAGE_KEY] = cloneStore(store);
  extra[PLUGIN_STORAGE_SOURCE_KEY] = sourceId;
  const next: SourceAuth = { extra };
  if (auth?.cookies !== undefined) next.cookies = auth.cookies;
  if (auth?.anonymous !== undefined) next.anonymous = auth.anonymous;
  return next;
}

export function createVeneraPluginStorage(sourceId: SourceId): VeneraPluginStorage {
  return {
    sourceId,
    load(auth, key) {
      assertSourceMatches(sourceId, auth);
      return readStore(auth)[key];
    },
    save(auth, key, value) {
      assertSourceMatches(sourceId, auth);
      const store = readStore(auth);
      store[key] = value;
      return withStore(sourceId, auth, store);
    },
    delete(auth, key) {
      assertSourceMatches(sourceId, auth);
      const store = readStore(auth);
      delete store[key];
      return withStore(sourceId, auth, store);
    },
    snapshot(auth) {
      assertSourceMatches(sourceId, auth);
      return readStore(auth);
    },
  };
}

export function authFromPluginStorage(
  sourceId: SourceId,
  store: Record<string, unknown>,
): SourceAuth {
  return {
    extra: {
      [PLUGIN_STORAGE_KEY]: cloneStore(store),
      [PLUGIN_STORAGE_SOURCE_KEY]: sourceId,
    },
  };
}
